/**
 * Camera: WASD/edge pan, wheel zoom, and the shake that sells a heavy hit.
 *
 * Zoom is expressed in screen pixels per world tile, which makes every other
 * calculation in the renderer trivial. "Tactical" is far enough out that the
 * whole map fits and units become war-map tokens; "intimate" is close enough to
 * see a shield come up.
 */

import { clamp, damp } from '../core/math.ts';
import { MAP_H, MAP_W } from '../sim/constants.ts';

export const ZOOM_TACTICAL = 13;
export const ZOOM_MIN = 9;
export const ZOOM_MAX = 74;
/** Below this, units are drawn as clean iconographic tokens instead of soldiers. */
export const ZOOM_TOKEN_THRESHOLD = 21;

export class Camera {
  /** Centre of view, in world tiles. */
  x = MAP_W / 2;
  y = MAP_H / 2;
  zoom = 30;

  private targetZoom = 30;
  private shakeAmp = 0;
  private shakeT = 0;
  private shakeX = 0;
  private shakeY = 0;
  private punch = 0;

  viewW = 1280;
  viewH = 720;

  /** Intensity slider, 0..1, from the options menu. */
  shakeScale = 1;

  resize(w: number, h: number): void {
    this.viewW = w;
    this.viewH = h;
  }

  setZoom(z: number, immediate = false): void {
    this.targetZoom = clamp(z, ZOOM_MIN, ZOOM_MAX);
    if (immediate) this.zoom = this.targetZoom;
  }

  zoomBy(factor: number, anchorScreenX?: number, anchorScreenY?: number): void {
    const before = this.targetZoom;
    const after = clamp(before * factor, ZOOM_MIN, ZOOM_MAX);
    if (after === before) return;

    if (anchorScreenX !== undefined && anchorScreenY !== undefined) {
      // Keep the world point under the cursor pinned while zooming.
      const wx = this.screenToWorldX(anchorScreenX);
      const wy = this.screenToWorldY(anchorScreenY);
      this.targetZoom = after;
      const nx = wx - (anchorScreenX - this.viewW / 2) / after;
      const ny = wy - (anchorScreenY - this.viewH / 2) / after;
      this.x = nx;
      this.y = ny;
    } else {
      this.targetZoom = after;
    }
  }

  pan(dxTiles: number, dyTiles: number): void {
    this.x += dxTiles;
    this.y += dyTiles;
  }

  /**
   * The two methods `ui/touch.ts`'s `Viewport` asks for, so that one gesture
   * recogniser drives both this camera and the strategic chart.
   *
   * They live here rather than in an adapter because this class is the thing
   * that knows the conversion: pixels-per-tile for the pan, and the view's own
   * size for turning a normalised pinch anchor into the screen point `zoomBy`
   * already takes. An adapter would be the same arithmetic written somewhere
   * that does not own either number.
   */
  panPixels(dxPx: number, dyPx: number): void {
    const z = this.effectiveZoom;
    if (z <= 0) return;
    this.pan(dxPx / z, dyPx / z);
  }

  zoomAt(factor: number, ax: number, ay: number): void {
    this.zoomBy(factor, ax * this.viewW, ay * this.viewH);
  }

  centreOn(x: number, y: number): void {
    this.x = x;
    this.y = y;
  }

  /** Add a shake impulse. `amp` is in screen pixels at the current zoom. */
  shake(amp: number, duration = 0.28): void {
    this.shakeAmp = Math.max(this.shakeAmp, amp * this.shakeScale);
    this.shakeT = Math.max(this.shakeT, duration);
  }

  /** A short inward zoom kick, for big impacts. */
  zoomPunch(amount = 0.035): void {
    this.punch = Math.max(this.punch, amount * this.shakeScale);
  }

  update(dt: number, seed: number): void {
    this.zoom = damp(this.zoom, this.targetZoom, 13, dt);

    if (this.shakeT > 0) {
      this.shakeT -= dt;
      const decay = Math.max(0, this.shakeT) / 0.28;
      const a = this.shakeAmp * decay * decay;
      // Deterministic-ish wobble driven by time, not RNG — the sim never sees this.
      this.shakeX = Math.sin(seed * 0.9 + this.shakeT * 61) * a;
      this.shakeY = Math.cos(seed * 1.3 + this.shakeT * 53) * a;
      if (this.shakeT <= 0) {
        this.shakeAmp = 0;
        this.shakeX = 0;
        this.shakeY = 0;
      }
    }

    if (this.punch > 0) this.punch = Math.max(0, this.punch - dt * 0.22);

    // Keep the view over the map, with a little slack at the edges.
    const halfW = this.viewW / 2 / this.zoom;
    const halfH = this.viewH / 2 / this.zoom;
    this.x = clamp(this.x, -4 + Math.min(halfW, MAP_W / 2), MAP_W + 4 - Math.min(halfW, MAP_W / 2));
    this.y = clamp(this.y, -4 + Math.min(halfH, MAP_H / 2), MAP_H + 4 - Math.min(halfH, MAP_H / 2));
  }

  get effectiveZoom(): number {
    return this.zoom * (1 + this.punch);
  }

  get offsetX(): number {
    return this.viewW / 2 - this.x * this.effectiveZoom + this.shakeX;
  }

  get offsetY(): number {
    return this.viewH / 2 - this.y * this.effectiveZoom + this.shakeY;
  }

  worldToScreenX(wx: number): number {
    return wx * this.effectiveZoom + this.offsetX;
  }

  worldToScreenY(wy: number): number {
    return wy * this.effectiveZoom + this.offsetY;
  }

  screenToWorldX(sx: number): number {
    return (sx - this.offsetX) / this.effectiveZoom;
  }

  screenToWorldY(sy: number): number {
    return (sy - this.offsetY) / this.effectiveZoom;
  }

  /** World-space rect currently visible, padded by `pad` tiles. */
  viewBounds(pad = 2): { x0: number; y0: number; x1: number; y1: number } {
    const halfW = this.viewW / 2 / this.effectiveZoom;
    const halfH = this.viewH / 2 / this.effectiveZoom;
    return {
      x0: this.x - halfW - pad,
      y0: this.y - halfH - pad,
      x1: this.x + halfW + pad,
      y1: this.y + halfH + pad,
    };
  }

  get isTactical(): boolean {
    return this.effectiveZoom < ZOOM_TOKEN_THRESHOLD;
  }
}
